import { useState, useMemo, useEffect } from 'react';

export interface PaginationConfig {
  pageSize: number;
  initialPage?: number;
}

export interface PaginationResult {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startItem: number;
  endItem: number;
  setPage: (page: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  setPageSize: (size: number) => void;
  setTotalItems: (total: number) => void;
  reset: () => void;
}

export function usePagination(
  initialTotalItems: number = 0,
  config: PaginationConfig = { pageSize: 25, initialPage: 1 }
): PaginationResult {
  const [currentPage, setCurrentPage] = useState(config.initialPage || 1);
  const [pageSize, setPageSizeState] = useState(config.pageSize);
  const [totalItems, setTotalItemsState] = useState(initialTotalItems);

  // Sync totalItems when initialTotalItems changes (e.g., from async query)
  useEffect(() => {
    setTotalItemsState(initialTotalItems);
  }, [initialTotalItems]);

  const totalPages = Math.ceil(totalItems / pageSize);
  
  const pagination = useMemo(() => {
    const hasNextPage = currentPage < totalPages;
    const hasPreviousPage = currentPage > 1;
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    return {
      currentPage,
      pageSize,
      totalPages,
      totalItems,
      hasNextPage,
      hasPreviousPage,
      startItem: totalItems === 0 ? 0 : startItem,
      endItem: totalItems === 0 ? 0 : endItem,
      setPage: (page: number) => {
        if (page >= 1 && page <= totalPages) {
          setCurrentPage(page);
        }
      },
      nextPage: () => {
        if (currentPage < totalPages) {
          setCurrentPage(prev => prev + 1);
        }
      },
      previousPage: () => {
        if (currentPage > 1) {
          setCurrentPage(prev => prev - 1);
        }
      },
      setPageSize: (size: number) => {
        setPageSizeState(size);
        // Reset to page 1 when changing page size
        setCurrentPage(1);
      },
      setTotalItems: (total: number) => {
        setTotalItemsState(total);
      },
      reset: () => {
        setCurrentPage(config.initialPage || 1);
        setPageSizeState(config.pageSize);
      }
    };
  }, [currentPage, pageSize, totalPages, totalItems, config.initialPage, config.pageSize]);

  return pagination;
}

export function calculatePaginationRange(from: number, to: number): { from: number; to: number } {
  return {
    from: Math.max(0, from),
    to: Math.max(0, to)
  };
}